import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { getAllStates } from '../constants/statesData';

const STATES = getAllStates();
const stateName = (id: number) => STATES.find(s => s.stateID === id)?.name || `State ${id}`;

// ── Types ────────────────────────────────────────────────────────────────────

interface MemberCycle {
  cycleid: number;
  cyclename: string;
  professionID: string;
  stateid: number;
  cyclestart?: string;
  cycleend?: string;
}

interface MemberRow {
  memberId: string;
  userId: string;
  name: string;
  email: string;
  professionID?: string;
  cycles: MemberCycle[];
  role: string;
}

interface Assignment {
  userId: string;
  cycleIds: number[];
  categories: string[];
}

type Step = 1 | 2 | 3;

// ── Component ─────────────────────────────────────────────────────────────────

const FirmSession: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>(1);
  const [members, setMembers] = useState<MemberRow[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  // professionID → ordered list of valid category tags
  const [profTagMap, setProfTagMap] = useState<Record<string, string[]>>({});

  // Step 1 — class details
  const [classData, setClassData] = useState({
    classname: '',
    classdate: new Date().toISOString().split('T')[0],
    classLU: '',
    provider: '',
    classwhere: '',
    categories: [] as string[],
    memo: '',
    islecture: true,
  });

  // Step 2 — selected members + per-member cycle + category overrides
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [assignments, setAssignments] = useState<Record<string, Assignment>>({});

  // Step 3 state is derived from above

  useEffect(() => {
    api.get('/api/company/members/cycles')
      .then(r => setMembers(r.data))
      .catch(() => setError('Failed to load team members.'))
      .finally(() => setLoadingMembers(false));

    api.get('/api/professions')
      .then(r => {
        const map: Record<string, string[]> = {};
        (r.data as any[]).forEach(p => {
          map[p.professionID] = (p.validCategoryTags || []).map((t: any) => t.tag as string);
        });
        setProfTagMap(map);
      })
      .catch(() => {/* non-fatal */});
  }, []);

  // ── helpers ──────────────────────────────────────────────────────────────

  const toggleMember = (m: MemberRow) => {
    const next = new Set(selected);
    if (next.has(m.userId)) {
      next.delete(m.userId);
    } else {
      next.add(m.userId);
      // Auto-init assignment
      if (!assignments[m.userId]) {
        const defaultCycles = m.cycles.length === 1 ? [m.cycles[0].cycleid] : [];
        // Only carry over global defaults that are valid for this member's profession
        const validTags = m.professionID ? (profTagMap[m.professionID] || []) : [];
        const filteredDefaults = validTags.length > 0
          ? classData.categories.filter(c => validTags.includes(c))
          : classData.categories;
        setAssignments(prev => ({
          ...prev,
          [m.userId]: { userId: m.userId, cycleIds: defaultCycles, categories: filteredDefaults },
        }));
      }
    }
    setSelected(next);
  };

  const toggleCycle = (userId: string, cycleId: number) => {
    setAssignments(prev => {
      const cur = prev[userId] || { userId, cycleIds: [], categories: [...classData.categories] };
      const ids = cur.cycleIds.includes(cycleId)
        ? cur.cycleIds.filter(id => id !== cycleId)
        : [...cur.cycleIds, cycleId];
      return { ...prev, [userId]: { ...cur, cycleIds: ids } };
    });
  };

  const updateCategories = (userId: string, cat: string) => {
    setAssignments(prev => {
      const cur = prev[userId] || { userId, cycleIds: [], categories: [] };
      const cats = cur.categories.includes(cat)
        ? cur.categories.filter(c => c !== cat)
        : [...cur.categories, cat];
      return { ...prev, [userId]: { ...cur, categories: cats } };
    });
  };

  const toggleGlobalCategory = (cat: string) => {
    setClassData(prev => {
      const cats = prev.categories.includes(cat)
        ? prev.categories.filter(c => c !== cat)
        : [...prev.categories, cat];
      return { ...prev, categories: cats };
    });
  };

  const step1Valid = classData.classname.trim() && classData.classdate && Number(classData.classLU) > 0;

  const step2Valid = selected.size > 0 && Array.from(selected).every(uid => {
    const a = assignments[uid];
    return a && a.cycleIds.length > 0;
  });

  const handleSubmit = async () => {
    setError('');
    setSubmitting(true);
    try {
      const payload = {
        classData: { ...classData, classLU: Number(classData.classLU) },
        assignments: Array.from(selected).map(uid => assignments[uid]),
      };
      await api.post('/api/company/class/bulk', payload);
      navigate('/team', { state: { toast: `Session "${classData.classname}" added for ${selected.size} member(s).` } });
    } catch (e: any) {
      setError(e.response?.data?.error || 'Submission failed. Please try again.');
      setSubmitting(false);
    }
  };

  // Global defaults for Step 1 (profession-agnostic starting point — per-member overrides in Step 2)
  const GLOBAL_CATEGORY_OPTIONS = ['HSW', 'ETHICS', 'ELECTIVE', 'ADA', 'CODE', 'ENERGY', 'SUSTAINABILITY'];

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="container mt-4" style={{ maxWidth: 860 }}>

      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="mb-0 fw-bold">Add Firm Session</h4>
          <p className="text-muted small mb-0">Apply one class/lecture to multiple team members at once</p>
        </div>
        <button className="btn btn-outline-secondary btn-sm" onClick={() => navigate('/team')}>← Back</button>
      </div>

      {/* Step indicator */}
      <div className="d-flex align-items-center mb-4 gap-2">
        {(['Class Details', 'Participants & Cycles', 'Review & Submit'] as const).map((label, i) => {
          const n = (i + 1) as Step;
          return (
            <React.Fragment key={n}>
              <div
                className="d-flex align-items-center gap-2"
                style={{ cursor: step > n ? 'pointer' : 'default' }}
                onClick={() => step > n && setStep(n)}
              >
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center fw-bold"
                  style={{
                    width: 28, height: 28, fontSize: '0.8rem',
                    background: step >= n ? 'var(--color-primary, #0f1f3d)' : '#dee2e6',
                    color: step >= n ? '#fff' : '#6c757d',
                  }}
                >
                  {step > n ? '✓' : n}
                </div>
                <span className={`small ${step >= n ? 'fw-semibold' : 'text-muted'}`}>{label}</span>
              </div>
              {i < 2 && <div style={{ flex: 1, height: 1, background: '#dee2e6' }} />}
            </React.Fragment>
          );
        })}
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {/* ── Step 1: Class Details ─────────────────────────────────────────── */}
      {step === 1 && (
        <div className="card shadow-sm">
          <div className="card-body p-4">
            <h6 className="fw-bold mb-3">Class / Lecture Details</h6>

            <div className="mb-3">
              <label className="form-label fw-semibold">Title <span className="text-danger">*</span></label>
              <input
                className="form-control"
                placeholder="e.g. Fire Safety Seminar"
                value={classData.classname}
                onChange={e => setClassData(p => ({ ...p, classname: e.target.value }))}
                autoFocus
              />
            </div>

            <div className="row g-3 mb-3">
              <div className="col-md-4">
                <label className="form-label fw-semibold">Date <span className="text-danger">*</span></label>
                <input
                  type="date"
                  className="form-control"
                  value={classData.classdate}
                  onChange={e => setClassData(p => ({ ...p, classdate: e.target.value }))}
                />
              </div>
              <div className="col-md-4">
                <label className="form-label fw-semibold">Credit Hours <span className="text-danger">*</span></label>
                <input
                  type="number"
                  className="form-control"
                  step="0.5"
                  min="0"
                  placeholder="e.g. 1.5"
                  value={classData.classLU}
                  onChange={e => setClassData(p => ({ ...p, classLU: e.target.value }))}
                />
                <div className="form-text">Each member's profession determines the unit label (LU, CPE, CLE, etc.)</div>
              </div>
              <div className="col-md-4">
                <label className="form-label fw-semibold">Type</label>
                <div className="d-flex gap-3 mt-2">
                  <div className="form-check">
                    <input className="form-check-input" type="radio" checked={classData.islecture} onChange={() => setClassData(p => ({ ...p, islecture: true }))} id="typeLecture" />
                    <label className="form-check-label" htmlFor="typeLecture">Lecture / In-person</label>
                  </div>
                  <div className="form-check">
                    <input className="form-check-input" type="radio" checked={!classData.islecture} onChange={() => setClassData(p => ({ ...p, islecture: false }))} id="typeSelf" />
                    <label className="form-check-label" htmlFor="typeSelf">Self-study</label>
                  </div>
                </div>
              </div>
            </div>

            <div className="row g-3 mb-3">
              <div className="col-md-6">
                <label className="form-label fw-semibold">Provider / Speaker</label>
                <input
                  className="form-control"
                  placeholder="e.g. AIA New York"
                  value={classData.provider}
                  onChange={e => setClassData(p => ({ ...p, provider: e.target.value }))}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label fw-semibold">Location</label>
                <input
                  className="form-control"
                  placeholder="e.g. Conference Room A"
                  value={classData.classwhere}
                  onChange={e => setClassData(p => ({ ...p, classwhere: e.target.value }))}
                />
              </div>
            </div>

            <div className="mb-3">
              <label className="form-label fw-semibold">Default Categories</label>
              <div className="form-text mb-2">These apply to all participants by default — you can override per member in Step 2.</div>
              <div className="d-flex flex-wrap gap-2">
                {GLOBAL_CATEGORY_OPTIONS.map(cat => (
                  <button
                    key={cat}
                    type="button"
                    className={`btn btn-sm ${classData.categories.includes(cat) ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => toggleGlobalCategory(cat)}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-4">
              <label className="form-label fw-semibold">Notes / Memo</label>
              <textarea
                className="form-control"
                rows={2}
                value={classData.memo}
                onChange={e => setClassData(p => ({ ...p, memo: e.target.value }))}
              />
            </div>

            <div className="d-flex justify-content-end">
              <button
                className="btn btn-primary"
                disabled={!step1Valid}
                onClick={() => setStep(2)}
              >
                Next: Select Participants →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Step 2: Participants & Cycles ─────────────────────────────────── */}
      {step === 2 && (
        <div className="card shadow-sm">
          <div className="card-body p-4">
            <h6 className="fw-bold mb-1">Who attended this session?</h6>
            <p className="text-muted small mb-3">Select members and assign each to the correct compliance cycle(s). Members without active cycles cannot be assigned.</p>

            {loadingMembers && <div className="text-center py-4"><div className="spinner-border spinner-border-sm" /></div>}

            {members.map(m => {
              const isSelected = selected.has(m.userId);
              const hasNoCycles = m.cycles.length === 0;
              const assignment = assignments[m.userId];

              return (
                <div
                  key={m.userId}
                  className={`border rounded-3 p-3 mb-3 ${isSelected ? 'border-primary bg-primary bg-opacity-10' : ''} ${hasNoCycles ? 'opacity-50' : ''}`}
                >
                  <div className="d-flex align-items-start gap-3">
                    <input
                      type="checkbox"
                      className="form-check-input mt-1 flex-shrink-0"
                      checked={isSelected}
                      disabled={hasNoCycles}
                      onChange={() => toggleMember(m)}
                      style={{ width: 18, height: 18 }}
                    />
                    <div className="flex-grow-1">
                      <div className="d-flex align-items-center gap-2 mb-1">
                        <span className="fw-semibold">{m.name}</span>
                        <span className="text-muted small">{m.email}</span>
                        {m.professionID && <span className="badge bg-secondary">{m.professionID}</span>}
                        {m.role === 'owner' && <span className="badge bg-primary">owner</span>}
                      </div>

                      {hasNoCycles && (
                        <p className="text-muted small mb-0">No active cycles — <a href="/cycles" target="_blank" rel="noreferrer">create a cycle</a> first.</p>
                      )}

                      {!hasNoCycles && isSelected && (
                        <div className="mt-2">
                          <div className="mb-2">
                            <span className="small fw-semibold text-muted">Assign to cycle(s):</span>
                            <div className="d-flex flex-wrap gap-2 mt-1">
                              {m.cycles.map(cyc => {
                                const checked = assignment?.cycleIds?.includes(cyc.cycleid);
                                return (
                                  <button
                                    key={cyc.cycleid}
                                    type="button"
                                    className={`btn btn-sm ${checked ? 'btn-primary' : 'btn-outline-secondary'}`}
                                    onClick={() => toggleCycle(m.userId, cyc.cycleid)}
                                  >
                                    {cyc.professionID} · {stateName(cyc.stateid)}
                                    <span className="ms-1 opacity-75" style={{ fontSize: '0.7rem' }}>
                                      {cyc.cyclename}
                                    </span>
                                  </button>
                                );
                              })}
                            </div>
                            {isSelected && (!assignment?.cycleIds?.length) && (
                              <div className="text-danger small mt-1">Please select at least one cycle.</div>
                            )}
                          </div>

                          <div>
                            <span className="small fw-semibold text-muted">Categories for {m.name}:</span>
                            <div className="d-flex flex-wrap gap-1 mt-1">
                              {(m.professionID && profTagMap[m.professionID]
                                ? profTagMap[m.professionID]
                                : GLOBAL_CATEGORY_OPTIONS
                              ).map(cat => (
                                <button
                                  key={cat}
                                  type="button"
                                  className={`btn btn-sm py-0 px-2 ${assignment?.categories?.includes(cat) ? 'btn-primary' : 'btn-outline-secondary'}`}
                                  style={{ fontSize: '0.72rem' }}
                                  onClick={() => updateCategories(m.userId, cat)}
                                >
                                  {cat}
                                </button>
                              ))}
                              {m.professionID && !profTagMap[m.professionID] && (
                                <span className="text-muted small fst-italic">Loading categories…</span>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      {!hasNoCycles && !isSelected && (
                        <p className="text-muted small mb-0">
                          {m.cycles.length} active cycle{m.cycles.length !== 1 ? 's' : ''} — check to include
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}

            <div className="d-flex justify-content-between mt-2">
              <button className="btn btn-outline-secondary" onClick={() => setStep(1)}>← Back</button>
              <button
                className="btn btn-primary"
                disabled={!step2Valid}
                onClick={() => setStep(3)}
              >
                Next: Review →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Step 3: Review & Submit ───────────────────────────────────────── */}
      {step === 3 && (
        <div className="card shadow-sm">
          <div className="card-body p-4">
            <h6 className="fw-bold mb-3">Review Before Submitting</h6>

            {/* Class summary */}
            <div className="p-3 rounded-3 mb-4" style={{ background: '#f8f9fa' }}>
              <div className="row g-2">
                <div className="col-12"><strong>{classData.classname}</strong></div>
                <div className="col-md-4 small text-muted">Date: <strong className="text-dark">{classData.classdate}</strong></div>
                <div className="col-md-4 small text-muted">Hours: <strong className="text-dark">{classData.classLU}</strong></div>
                <div className="col-md-4 small text-muted">Type: <strong className="text-dark">{classData.islecture ? 'Lecture' : 'Self-study'}</strong></div>
                {classData.provider && <div className="col-12 small text-muted">Provider: <strong className="text-dark">{classData.provider}</strong></div>}
                {classData.categories.length > 0 && (
                  <div className="col-12 small text-muted">Default categories: {classData.categories.map(c => (
                    <span key={c} className="badge bg-primary me-1">{c}</span>
                  ))}</div>
                )}
              </div>
            </div>

            {/* Per-member breakdown */}
            <h6 className="text-muted small fw-semibold mb-2">{selected.size} PARTICIPANT{selected.size !== 1 ? 'S' : ''}</h6>
            <table className="table table-sm table-bordered mb-4">
              <thead className="table-light">
                <tr>
                  <th>Member</th>
                  <th>Cycle(s)</th>
                  <th>Categories</th>
                  <th>Hours</th>
                </tr>
              </thead>
              <tbody>
                {Array.from(selected).map(uid => {
                  const m = members.find(x => x.userId === uid);
                  const a = assignments[uid];
                  const cycleLabels = a.cycleIds.map(cid => {
                    const cyc = m?.cycles.find(c => c.cycleid === cid);
                    return cyc ? `${cyc.professionID}·${stateName(cyc.stateid)}` : cid;
                  }).join(', ');
                  return (
                    <tr key={uid}>
                      <td className="fw-medium">{m?.name}</td>
                      <td className="small">{cycleLabels}</td>
                      <td className="small">
                        {a.categories.length
                          ? a.categories.map(c => <span key={c} className="badge bg-primary me-1">{c}</span>)
                          : <span className="text-muted">None</span>}
                      </td>
                      <td>{classData.classLU}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <div className="d-flex justify-content-between">
              <button className="btn btn-outline-secondary" onClick={() => setStep(2)}>← Back</button>
              <button
                className="btn btn-success px-5"
                disabled={submitting}
                onClick={handleSubmit}
              >
                {submitting ? 'Saving…' : `Submit — Add to ${selected.size} Member${selected.size !== 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FirmSession;
